import type { ExportTable } from '../../widget-actions';

// The charts' props, not what they drew: the hooks downsample and rescale for the plot.

type TimeSeriesTable = {
  name: string;
  series: { label: string; data: { x: number; y: number }[] }[];
};

/** One row per instant, one column per series; rows are the union, an absent point left empty. */
export const buildTimeSeriesTable = ({ name, series }: TimeSeriesTable): ExportTable => {
  const instants = [...new Set(series.flatMap((serie) => serie.data.map((point) => point.x)))].sort((a, b) => a - b);
  const pointsBySeries = series.map((serie) => new Map(serie.data.map((point) => [point.x, point.y])));

  return {
    name,
    columns: ['timestamp', ...series.map((serie) => serie.label)],
    rows: instants.map((instant) => [
      new Date(instant).toISOString(),
      ...pointsBySeries.map((points) => points.get(instant) ?? null),
    ]),
  };
};

type CategoryTable = {
  name: string;
  /** Header of the first column — what the categories are. */
  categoryColumn: string;
  categories: (string | number)[];
  series: { label: string; values: number[] }[];
};

/** One row per category, one column per series — bars and histogram buckets alike. */
export const buildCategoryTable = ({ name, categoryColumn, categories, series }: CategoryTable): ExportTable => ({
  name,
  columns: [categoryColumn, ...series.map((serie) => serie.label)],
  rows: categories.map((category, index) => [category, ...series.map((serie) => serie.values[index] ?? null)]),
});

type StatusGridTable = {
  name: string;
  /** Slot starts, unix timestamps in milliseconds. */
  columns: number[];
  rows: { label: string; cells: (string | null)[] }[];
};

/**
 * One row per slot, one column per entity — a status grid read the way its chart is, down
 * the time axis. The transpose of the grid on screen, which puts time in the first column
 * as every other export here does.
 */
export const buildStatusGridTable = ({ name, columns, rows }: StatusGridTable): ExportTable => ({
  name,
  columns: ['timestamp', ...rows.map((row) => row.label)],
  rows: columns.map((instant, index) => [
    new Date(instant).toISOString(),
    ...rows.map((row) => row.cells[index] ?? null),
  ]),
});
